import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInterval} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ColorDot} from '../../../../libs/shared/ui/src/components/utility/colorDot';
import {PulsatingColorDot} from '../../../../libs/shared/ui/src/components/utility/pulsatingColorDot';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {renderDurationValue} from '../../helpers/metricRenderers/durationMetricRenderer';

/*
 * Props.
 */

interface LiveDashboardLastUpdatedAtTimerProps {
  reportFetchedAtMillis: number;
}

/*
 * Styles.
 */

const StyledFlexDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledReportSubTextDiv = styled.div`
  align-self: baseline;

  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const LiveDashboardLastUpdatedAtTimer: FC<LiveDashboardLastUpdatedAtTimerProps> = ({
  reportFetchedAtMillis,
}) => {
  const theme = useUserTheme();

  const [durationCeiledToTenSeconds, setDurationCeiledToTenSeconds] = useState(
    computeAgeCeiledToTenSeconds(reportFetchedAtMillis),
  );

  const [setIntervalFunc, clearIntervalFunc] = useInterval();

  useEffect(() => {
    setDurationCeiledToTenSeconds(computeAgeCeiledToTenSeconds(reportFetchedAtMillis));

    setIntervalFunc(
      () => setDurationCeiledToTenSeconds(computeAgeCeiledToTenSeconds(reportFetchedAtMillis)),
      1_000,
    );

    return clearIntervalFunc;
  }, [clearIntervalFunc, reportFetchedAtMillis, setIntervalFunc]);

  // TODO(ldemarne): Consider also using a "network is offline" source of truth.
  const isDashboardContentStale = durationCeiledToTenSeconds >= LIVE_DASHBOARD_STALE_REPORT_AGE_SECONDS;

  if (isDashboardContentStale) {
    const minuteCount = Math.floor(durationCeiledToTenSeconds / 60);

    return (
      <StyledFlexDiv>
        <ColorDot color={theme.greys.shade70} size={8} />
        <StyledReportSubTextDiv>
          <Trans id="live-dashboard-header-last-updated-at-timer-stale">
            Connection issue, last updated over {minuteCount}m ago
          </Trans>
        </StyledReportSubTextDiv>
        <TooltipCoordinator
          placement="top"
          render={() => (
            <Trans id="live-dashboard-header-check-connection-tooltip">
              Try refreshing Front or checking your connection.
            </Trans>
          )}
        />
      </StyledFlexDiv>
    );
  }

  const renderedDuration = maybeRemoveUglyZeroSeconds(
    renderDurationValue({
      value: durationCeiledToTenSeconds,
    }),
  );

  return (
    <StyledFlexDiv>
      <PulsatingColorDot color={theme.palette.green.shade40} size={8} />
      <StyledReportSubTextDiv>
        <Trans id="live-dashboard-header-last-updated-at-timer">
          Live data, last updated {renderedDuration} ago
        </Trans>
      </StyledReportSubTextDiv>
    </StyledFlexDiv>
  );
};

/*
 * Constants.
 */

const LIVE_DASHBOARD_STALE_REPORT_AGE_SECONDS = 2 * 60; // 2 minutes.

/*
 * Helpers.
 */

function computeAgeCeiledToTenSeconds(fetchedAtMillis: number) {
  const durationInSeconds = (new Date().getTime() - fetchedAtMillis) / 1_000;
  return Math.ceil(durationInSeconds / 10) * 10;
}

// Because `renderDurationValue(60)` returns 1m 0s while we want 1m.
const uglyZeroSeconds = ' 0s';

function maybeRemoveUglyZeroSeconds(renderedDuration: string) {
  if (!renderedDuration.endsWith(uglyZeroSeconds)) {
    return renderedDuration;
  }

  return renderedDuration.slice(0, -uglyZeroSeconds.length);
}
